import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import { get } from 'lodash';
import { Translation, TranslationWithParams } from 'src/app/i18n';
import { useRequest } from 'ahooks';
import { getConfigurations } from 'src/app/common/utils';
import {
  fetchRegistrationDetail,
  updateRegistrationStatus,
  editRegistrationResult,
  editExamineeDetails,
} from '../../../network/license-exam-crud';
import { examRegistrationBasePath } from '../registration/registration-routes';
import { useHistory } from 'react-router-dom';
import { RegistrationApprovalEventsEnum, RegistrationApprovalStatesEnum } from '../../../enum/license-exam.enum';
import { useForm } from 'src/app/common/components/Form';
import { statusType, UpdateExamineeDetails, UpdateExamResult } from '../../../types/license-exam-types';
import { AlertType, appendAlertItem } from '@pruforce/common-adminweb-sdk';

interface HookProps {
  id: string;
  enableUpdate: boolean;
}

export const useRegistrationDetail = ({ id, enableUpdate }: HookProps) => {
  const history = useHistory();
  const [form] = useForm();
  const [editExamineeform] = useForm();

  // redux
  const dispatch = useDispatch();
  const [dialogState, setDialogState] = useState<{ open: boolean; header: string }>({ open: false, header: '' });
  const [action, setAction] = useState<RegistrationApprovalEventsEnum>(RegistrationApprovalEventsEnum.approve);
  const [remark, setRemark] = useState<string>('');
  const [editing, setEditing] = useState<boolean>(false);
  const [editingExamineeDetails, setEditingExamineeDetails] = useState<boolean>(false);

  // get runtime-config
  const enableApproval: boolean = useMemo(() => {
    const config = getConfigurations();
    const enableApproval = get(config, 'Recruitment.enableExamApproval', false);
    return enableApproval;
  }, []);

  const {
    data,
    loading: loadingRegistrationDetail,
    run: runFetchDetail,
  } = useRequest(() => fetchRegistrationDetail(id, dispatch), {
    manual: true,
  });

  const { loading: loadingApprove, run: runUpdateRegistrationStatus } = useRequest(
    () =>
      updateRegistrationStatus(
        {
          registrationApprovalEvents: action,
          remark: remark,
        },
        id,
        dispatch,
      )
        .then(() => {
          handleCloseDialog();
          history.push(`${examRegistrationBasePath}/list`);
        })
        .catch(() => {
          handleCloseDialog();
        }),
    {
      manual: true,
    },
  );

  const { loading: loadingUpdateResult, run: runEditRegistrationResult } = useRequest(
    (values: UpdateExamResult) =>
      editRegistrationResult(values, id).then(() => {
        runFetchDetail();
        setEditing(false);
        setEditingExamineeDetails(false);
      }),
    {
      manual: true,
    },
  );

  const { loading: loadingEditExamineeDetails, run: runEditExamineeDetails } = useRequest(
    (values: UpdateExamineeDetails) =>
      editExamineeDetails(values, id)
        .then(() => {
          dispatch(
            appendAlertItem([
              {
                severity: AlertType.SUCCESS,
                title: 'Success',
                content: 'Update examinee details successfully',
              },
            ]),
          );
          setEditing(false);
          setEditingExamineeDetails(false);
          runFetchDetail();
        })
        .catch((error) => {
          console.error(error);
          dispatch(
            appendAlertItem([
              {
                severity: AlertType.ERROR,
                title: 'Error',
                content: `Update examinee details failed`,
              },
            ]),
          );
        }),
    {
      manual: true,
    },
  );

  useEffect(() => {
    runFetchDetail();
  }, [id]);

  const examineeDetails = useMemo(() => {
    return {
      firstname: data?.firstname || '',
      lastname: data?.lastname || '',
      birthday: (data?.birthday as string) || '',
      identityDocumentNumber: (data?.identityDocumentNumber as string) || '',
      hkId: (data?.hkId as string) || '',
      localName: (data?.localName as string) || null,
    };
  }, [data]);

  const initialValues = useMemo(
    () => ({
      result: data?.result || null,
      score: data?.score || '',
    }),
    [data],
  );

  const statusLabel = useMemo(() => {
    if (data && data.registrationApprovalStates) {
      return Translation(
        `recruitment.exam.registration.status.${
          RegistrationApprovalStatesEnum[data.registrationApprovalStates as statusType]
        }`,
      );
    }
    return '-';
  }, [data]);

  const showResult = useMemo(
    () =>
      data?.registrationApprovalStates === RegistrationApprovalStatesEnum.REGISTERED.toUpperCase() ||
      data?.result != null ||
      data?.score != null,
    [data],
  );

  const showCancelButton = useMemo(
    () =>
      (enableUpdate &&
        data?.registrationApprovalStates === RegistrationApprovalStatesEnum.PENDING_APPROVAL.toUpperCase()) ||
      data?.registrationApprovalStates === RegistrationApprovalStatesEnum.REGISTERED.toUpperCase(),
    [data],
  );

  const showApprovalButtons = useMemo(
    () =>
      enableUpdate &&
      enableApproval &&
      data?.registrationApprovalStates === RegistrationApprovalStatesEnum.PENDING_APPROVAL.toUpperCase(),
    [data],
  );

  const handleCloseDialog = useCallback(() => {
    setDialogState({ open: false, header: '' });
    setRemark('');
  }, []);

  const handlePress = useCallback((action: RegistrationApprovalEventsEnum) => {
    setDialogState({
      open: true,
      header: TranslationWithParams('recruitment.exam.registration.dialogTitle', {
        action: Translation(`recruitment.exam.registration.${action.toLowerCase()}`).toLowerCase(),
      }),
    });
    setAction(action);
  }, []);

  const handleSaveEditResult = useCallback((values: UpdateExamResult) => {
    runEditRegistrationResult(values);
  }, []);

  const handleConfirm = useCallback(() => {
    runUpdateRegistrationStatus();
  }, []);

  const handleSaveExamineeDetails = useCallback((values: UpdateExamineeDetails) => {
    if (values) {
      const body = {
        ...values,
        identityDocumentType: values?.hkId === 'Y' ? 'HKID' : 'Passport',
      };
      delete body.hkId;
      runEditExamineeDetails(body);
    }
  }, []);
  return {
    loadingRegistrationDetail,
    data,
    enableApproval,
    handlePress,
    dialogState,
    handleCloseDialog,
    action,
    showResult,
    showCancelButton,
    showApprovalButtons,
    editing,
    setEditing,
    handleSaveEditResult,
    handleConfirm,
    remark,
    setRemark,
    form,
    initialValues,
    loadingUpdateResult,
    loadingApprove,
    statusLabel,
    editingExamineeDetails,
    setEditingExamineeDetails,
    handleSaveExamineeDetails,
    editExamineeform,
    examineeDetails,
    loadingEditExamineeDetails,
  };
};
